import { Badge, Card, Group, Text, Center } from "@mantine/core";
import type { FC, PropsWithChildren, ReactNode } from "react";
import { ProductIcon } from "./ProductIcon.js";
import type { TimeInterval } from "../../../types/time.js";
import { FormattedMessage } from "react-intl";
import { ErrorAlert } from "./ErrorAlert.js";
import { ChartLoader } from "./ChartLoader.js";

export interface To {
  to: Date;
}

export interface FromTo extends To {
  from: Date;
}

export interface BasicUsageParameters extends FromTo {
  interval?: TimeInterval;
}

export interface SpecificUsageParameters extends BasicUsageParameters {
  apiKeyId?: number; // API-specific
}

interface PropsForUsageCharts {
  addToDetailedView: () => void;
}

export type BasicUsageChartProps = BasicUsageParameters & PropsForUsageCharts;
export type SpecificUsageChartProps = SpecificUsageParameters & PropsForUsageCharts;

type UsageChartWrapperProps = PropsWithChildren & PropsForUsageCharts & {
  title: string;
  icon: ReactNode;
  iconColor: string;
  hasUsage: boolean;
  hasError: boolean;
  isLoading: boolean;
  isApproachingLimit: boolean;
};

export const UsageChartWrapper: FC<UsageChartWrapperProps> = ({
  title,
  icon,
  iconColor,
  children,
  hasUsage,
  hasError,
  isLoading,
  addToDetailedView,
  isApproachingLimit,
}) => {
  if (isLoading) return <ChartLoader />;

  let content: ReactNode = children;
  if (hasError) {
    content = (
      <Center h="100%">
        <ErrorAlert>
          <FormattedMessage
            defaultMessage="Error fetching usage for this metric"
            id="chart.error"
          />
        </ErrorAlert>
      </Center>
    );
  }
  else if (!hasUsage) {
    content = (
      <Center h="100%">
        <Text fz="sm">
          <FormattedMessage
            defaultMessage="No usage recorded for this period"
            id="chart.noUsage"
          />
        </Text>
      </Center>
    );
  }

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      h={320}
      onClick={addToDetailedView}
      className="pointer"
      style={{ overflow: "visible" }}
    >
      <Group wrap="nowrap" gap={10}>
        {icon && (
          // The design prescribes a 20px container and 6px padding but that's too small.
          // A 30px container and 20px icon looks good to me.
          <ProductIcon iconColor={iconColor} size={30}>
            {icon}
          </ProductIcon>
        )}

        <Text fw={600} size="md" className="underline-on-hover grow nowrap">
          {title}
        </Text>

        {isApproachingLimit && (
          <Badge
            variant="outline"
            color="warning"
            fz="xs"
            px={12}
            py={4}
            styles={{
              label: {
                textTransform: "none",
                fontWeight: 400,
              },
            }}
          >
            <FormattedMessage
              defaultMessage="Approaching limit"
              id="chart.approachingLimit"
            />
          </Badge>
        )}
      </Group>

      {content}
    </Card>
  );
};
